import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

type BillingTab = 'payments' | 'transactions' | 'usage';

interface UsageSummary {
  total_cost: number;
  compute_hours: number;
  storage_gb: number;
  data_transfer_gb: number;
}

interface UsageData {
  date: string;
  compute_hours: number;
  cost: number;
  instances_used: number;
}

interface ResourceUsage {
  name: string;
  type: 'compute' | 'storage' | 'data-transfer';
  usage_amount: number;
  unit: string;
  cost: number;
  percentage: number;
}

interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}

interface StripeInvoice {
  id: string;
  amount_paid: number;
  currency: string;
  status: string;
  created: number;
  description?: string;
}

interface BalanceStatus {
  current_balance: number;
  can_launch: boolean;
  is_low_balance: boolean;
  requires_topup: boolean;
  min_launch_balance: number;
  low_balance_threshold: number;
  shutdown_buffer: number;
}

interface BalanceTransaction {
  id: number;
  type: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  description: string;
  created_at: string;
}

export default function BillingPage() {
  const [activeTab, setActiveTab] = useState<BillingTab>('payments');
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [balanceStatus, setBalanceStatus] = useState<BalanceStatus | null>(null);
  const [balanceError, setBalanceError] = useState(false);
  const [balanceTransactions, setBalanceTransactions] = useState<BalanceTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [topupError, setTopupError] = useState<string | null>(null);
  const [successMessageType, setSuccessMessageType] = useState<'payment' | 'topup' | 'setup'>('payment');
  const [successAmount, setSuccessAmount] = useState<string>('');
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState<number>(10);
  // Usage tab state
  const [usageHistory, setUsageHistory] = useState<UsageData[]>([]);
  const [resourceBreakdown, setResourceBreakdown] = useState<ResourceUsage[]>([]);
  const [usageTimeRange, setUsageTimeRange] = useState('7d');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      // If we landed here from a Stripe success redirect, persist the
      // session_id so LoginPage can resume confirm-checkout after re-auth.
      // Without this, the charge is taken but never credited.
      const pendingTopup = searchParams.get('topup_success');
      const pendingSessionId = searchParams.get('session_id');
      const pendingAmount = searchParams.get('amount');
      if (pendingTopup === 'true' && pendingSessionId) {
        sessionStorage.setItem('pending_topup_session_id', pendingSessionId);
        if (pendingAmount) sessionStorage.setItem('pending_topup_amount', pendingAmount);
      }
      navigate('/login');
      return;
    }

    // Check for Stripe success/cancel parameters
    const success = searchParams.get('success');
    const setupSuccess = searchParams.get('setup_success');
    const topupSuccess = searchParams.get('topup_success');
    const amount = searchParams.get('amount');

    if (setupSuccess === 'true') {
      setSuccessMessageType('setup');
      setShowSuccessMessage(true);
      setSearchParams({});
      setTimeout(() => setShowSuccessMessage(false), 5000);
      fetchBillingData();
    } else if (success === 'true') {
      setSuccessMessageType('payment');
      setShowSuccessMessage(true);
      // Clear the URL parameters
      setSearchParams({});
      // Auto-hide success message after 5 seconds
      setTimeout(() => setShowSuccessMessage(false), 5000);
      // Force a refresh to load the new payment methods
      fetchBillingData();
    } else if (topupSuccess === 'true') {
      // Get session_id from URL to confirm the payment
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        // Confirm the checkout session to process the payment
        confirmCheckoutSession(sessionId, amount || '');
      } else {
        // Fallback if no session_id (shouldn't happen)
        setSuccessMessageType('topup');
        setSuccessAmount(amount || '');
        setShowSuccessMessage(true);
      }
      // Clear the URL parameters
      setSearchParams({});
      // Auto-hide success message after 5 seconds
      setTimeout(() => setShowSuccessMessage(false), 5000);
      // Force a refresh to load the updated balance
      fetchBillingData();
    } else {
      fetchBillingData();
    }
  }, [navigate, searchParams, setSearchParams]);


  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };

      // Safety net: reconcile any paid-but-uncredited Stripe top-ups before
      // showing the balance. Idempotent and usually a no-op.
      try {
        await axios.post(`${getApiUrl()}/api/balance/topup/reconcile`, {}, { headers });
      } catch (reconcileErr) {
        console.warn('Top-up reconcile skipped:', reconcileErr);
      }

      // Fetch current month usage
      const usageResponse = await axios.get(`${getApiUrl()}/api/usage/current-month`, { headers });
      setUsage(usageResponse.data);

      // Fetch balance status (with error handling)
      try {
        const balanceResponse = await axios.get(`${getApiUrl()}/api/balance/status`, { headers });
        console.log('Balance response:', balanceResponse.data);
        setBalanceStatus(balanceResponse.data);
        setBalanceError(false);
      } catch (balErr) {
        console.error('Error fetching balance status:', balErr);
        setBalanceStatus(null);
        setBalanceError(true);
      }

      // Fetch balance transactions (with error handling)
      try {
        const transactionsResponse = await axios.get(`${getApiUrl()}/api/balance/transactions?limit=10`, { headers });
        console.log('Transactions response:', transactionsResponse.data);
        setBalanceTransactions(transactionsResponse.data.transactions || []);
      } catch (transactionError) {
        console.error('Error fetching balance transactions:', transactionError);
        setBalanceTransactions([]);
      }

      // Check if user has Stripe customer and fetch payment data
      const userResponse = await axios.get(`${getApiUrl()}/auth/me`, { headers });
      const hasStripe = Boolean(userResponse.data.stripe_customer_id);
      console.log('User response:', userResponse.data);
      console.log('Has Stripe customer:', hasStripe, 'Customer ID:', userResponse.data.stripe_customer_id);

      if (hasStripe) {
        // Fetch payment methods
        try {
          const paymentMethodsResponse = await axios.get(`${getApiUrl()}/api/stripe/payment-methods`, { headers });
          console.log('Payment methods response:', paymentMethodsResponse.data);
          setPaymentMethods(paymentMethodsResponse.data.payment_methods || []);
        } catch (pmError) {
          console.error('Error fetching payment methods:', pmError);
          setPaymentMethods([]);
        }

        // Fetch invoices
        try {
          const invoicesResponse = await axios.get(`${getApiUrl()}/api/stripe/invoices`, { headers });
          console.log('Invoices response:', invoicesResponse.data);
          setInvoices(invoicesResponse.data.invoices || []);
        } catch (invoiceError) {
          console.error('Error fetching invoices:', invoiceError);
          setInvoices([]);
        }
      }

    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch daily usage trend
      const days = usageTimeRange === '7d' ? 7 : usageTimeRange === '30d' ? 30 : 90;
      const trendResponse = await axios.get(`${getApiUrl()}/api/usage/daily-trend?days=${days}`, { headers });
      setUsageHistory(trendResponse.data.daily_usage || []);

      // Fetch resource breakdown
      const breakdownResponse = await axios.get(`${getApiUrl()}/api/usage/breakdown`, { headers });
      setResourceBreakdown(breakdownResponse.data.resource_breakdown || []);
    } catch (error) {
      console.error('Error fetching usage data:', error);
      setUsageHistory([]);
      setResourceBreakdown([]);
    }
  };

  // Fetch usage data when tab changes to usage
  useEffect(() => {
    if (activeTab === 'usage') {
      fetchUsageData();
    }
  }, [activeTab, usageTimeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmCheckoutSession = async (sessionId: string, amount: string) => {
    setTopupError(null);
    try {
      const token = localStorage.getItem('authToken');

      // First, try the manual confirmation endpoint
      const response = await axios.post(
        `${getApiUrl()}/api/balance/topup/confirm-checkout`,
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessMessageType('topup');
        setSuccessAmount(amount);
        setShowSuccessMessage(true);

        // Update balance status
        if (response.data.new_balance) {
          setBalanceStatus(prev => prev ? {
            ...prev,
            current_balance: response.data.new_balance
          } : null);
        }
        return;
      }
    } catch (error: any) {
      console.error('Error confirming checkout session:', error);
      // If the endpoint doesn't exist, try the debug endpoint as fallback
      if (error.response?.status === 404) {
        try {
          const authToken = localStorage.getItem('authToken');
          const debugResponse = await axios.post(
            `${getApiUrl()}/api/balance/debug-check-recent-payments`,
            {},
            { headers: { Authorization: `Bearer ${authToken}` } }
          );

          if (debugResponse.data.processed_count > 0) {
            setSuccessMessageType('topup');
            setSuccessAmount(amount);
            setShowSuccessMessage(true);

            // Update balance status
            if (debugResponse.data.current_balance) {
              setBalanceStatus(prev => prev ? {
                ...prev,
                current_balance: debugResponse.data.current_balance
              } : null);
            }
            return;
          }
        } catch (debugError) {
          console.error('Debug recovery also failed:', debugError);
        }
      }
    }
    // Reached only when neither the confirm call nor the recovery path
    // credited the top-up. Surface it instead of failing silently — if the
    // charge did go through, the webhook reconciles and a refresh will show it.
    setTopupError(
      `We couldn't confirm your${amount ? ` $${amount}` : ''} top-up. ` +
      `If your card was charged, your balance will update automatically within a few minutes — ` +
      `refresh this page to check.`
    );
  };

  const handleSetupPayments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${getApiUrl()}/api/stripe/create-checkout-session`,
        { mode: 'setup' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to redirect to Stripe. Please try again.');
    }
  };

  const handleManagePayments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${getApiUrl()}/api/stripe/create-customer-portal-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      console.error('Error creating customer portal session:', error);
      alert('Failed to redirect to Stripe. Please try again.');
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId: string) => {
    if (paymentMethods.length <= 1) {
      alert('You must have at least one payment method on file.');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(
        `${getApiUrl()}/api/stripe/payment-methods/${paymentMethodId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh payment methods
      fetchBillingData();
    } catch (error) {
      console.error('Error removing payment method:', error);
      alert('Failed to remove payment method. Please try again.');
    }
  };

  const handleTopUp = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Create Stripe checkout session for top-up
      const response = await axios.post(
        `${getApiUrl()}/api/balance/topup/create-checkout-session`,
        { amount: topUpAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Error creating top-up checkout:', error);
      alert('Failed to create top-up session. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Billing & Usage</h1>
            <p className="text-gray-400">
              Monitor your costs and usage across all cloud providers
            </p>
          </div>
          <div className="flex space-x-3">
            {paymentMethods.length > 0 && (
              <button 
                onClick={handleManagePayments}
                className="px-4 py-2 bg-[#635BFF] hover:bg-[#5A52E5] text-white rounded-lg text-sm font-semibold transition-all duration-200 inline-flex items-center space-x-2 shadow-sm hover:shadow-md"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.274 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
                <span>Manage Billing</span>
              </button>
            )}
          </div>
        </div>

        {/* Success Message */}
        {showSuccessMessage && (
          <div className="bg-green-900/20 border border-green-800/50 rounded-xl p-4 mb-8">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                {successMessageType === 'setup' ? (
                  <>
                    <h3 className="text-lg font-medium text-white mb-1">Payment Method Added Successfully!</h3>
                    <p className="text-green-300 text-sm">
                      Your billing has been set up. You can now add funds and launch GPU instances.
                    </p>
                  </>
                ) : successMessageType === 'payment' ? (
                  <>
                    <h3 className="text-lg font-medium text-white mb-1">Payment Method Added Successfully!</h3>
                    <p className="text-green-300 text-sm">
                      Your payment method has been securely saved. You can now use cloud resources and will be billed automatically.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-medium text-white mb-1">Balance Top-up Successful!</h3>
                    <p className="text-green-300 text-sm">
                      ${successAmount} has been added to your account balance. You can now launch GPU instances.
                    </p>
                  </>
                )}
              </div>
              <button
                onClick={() => setShowSuccessMessage(false)}
                className="text-green-400 hover:text-green-300 transition-colors ml-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Top-up Confirmation Error Banner */}
        {topupError && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 mb-8" role="alert">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-white mb-1">Top-up not confirmed</h3>
                <p className="text-red-300 text-sm">{topupError}</p>
              </div>
              <button
                onClick={() => setTopupError(null)}
                className="text-red-400 hover:text-red-300 transition-colors ml-4"
                aria-label="Dismiss"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Balance Error Banner */}
        {balanceError && !balanceStatus && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 mb-8">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-white mb-1">Unable to load balance</h3>
                <p className="text-red-300 text-sm">
                  Could not retrieve your account balance. Please refresh the page or try again later.
                </p>
              </div>
              <button
                onClick={() => fetchBillingData()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors ml-4"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Account Balance Section */}
        {balanceStatus && (
          <div className={`rounded-xl border p-6 mb-8 ${
            balanceStatus.is_low_balance 
              ? 'bg-red-900/20 border-red-800/50' 
              : balanceStatus.requires_topup
              ? 'bg-orange-900/20 border-orange-800/50'
              : 'bg-gray-900 border-gray-800'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-2xl font-bold text-white">
                    Account Balance: ${balanceStatus.current_balance.toFixed(2)}
                  </h3>
                  {balanceStatus.is_low_balance && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-500/20 text-red-400">
                      Low Balance
                    </span>
                  )}
                  {balanceStatus.requires_topup && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-500/20 text-orange-400">
                      Top-up Required
                    </span>
                  )}
                  {balanceStatus.can_launch && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-400">
                      Ready to Launch
                    </span>
                  )}
                </div>
                <p className="text-gray-400 text-sm">
                  {balanceStatus.requires_topup 
                    ? `Minimum $${balanceStatus.min_launch_balance.toFixed(2)} required to launch GPU instances`
                    : balanceStatus.is_low_balance
                    ? `Balance is low. Consider adding funds to avoid service interruption.`
                    : 'Your account is funded and ready for GPU launches'
                  }
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowTopUpModal(true)}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors inline-flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add Funds</span>
                </button>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-700">
              <div className="text-center">
                <div className="text-lg font-semibold text-white">${balanceStatus.min_launch_balance.toFixed(2)}</div>
                <div className="text-xs text-gray-400">Min. Launch</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-white">${balanceStatus.low_balance_threshold.toFixed(2)}</div>
                <div className="text-xs text-gray-400">Low Balance Alert</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-white">${balanceStatus.shutdown_buffer.toFixed(2)}</div>
                <div className="text-xs text-gray-400">Auto-Stop Buffer</div>
              </div>
            </div>
          </div>
        )}

        {/* Top-Up Modal */}
        {showTopUpModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Add Funds</h3>
                <button
                  onClick={() => setShowTopUpModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Amount to Add
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="500"
                    step="5"
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(Number(e.target.value))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                {/* Quick amount buttons */}
                <div className="grid grid-cols-4 gap-2">
                  {[5, 10, 25, 50].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setTopUpAmount(amount)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        topUpAmount === amount
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleTopUp}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Add ${topUpAmount}
                </button>
                <button
                  onClick={() => setShowTopUpModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-800 mb-8">
          {[
            { key: 'payments' as const, label: 'Payment Methods' },
            { key: 'transactions' as const, label: 'Transactions' },
            { key: 'usage' as const, label: 'Usage Analytics' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.key
                  ? 'text-white'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'payments' && (
          <>
            {/* Billing Setup Section */}
            {paymentMethods.length === 0 && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-8 mb-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-[#635BFF]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#635BFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Set Up Billing</h3>
              <p className="text-gray-400">
                Add a payment method to start using cloud resources.
              </p>
            </div>
            
            {/* Stripe-branded button following their design guidelines */}
            <button
              onClick={handleSetupPayments}
              className="group relative px-8 py-4 bg-[#635BFF] hover:bg-[#5A52E5] active:bg-[#4E46D9] text-white rounded-lg font-semibold transition-all duration-200 inline-flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {/* Stripe logo/icon */}
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.274 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
              </svg>
              <span>Continue with Stripe</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            
            {/* Stripe trust indicators */}
            <div className="mt-6 flex items-center justify-center space-x-6 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>SSL Secured</span>
              </div>
              <div className="flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>PCI Compliant</span>
              </div>
              <div className="flex items-center space-x-1">
                <span>Powered by</span>
                <span className="font-semibold text-[#635BFF]">Stripe</span>
              </div>
            </div>
          </div>
        )}

        {/* Usage Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Current Month</p>
                <p className="text-3xl font-bold text-white">${usage?.total_cost?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-sm text-gray-500">Pay-as-you-go pricing</span>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Compute Hours</p>
                <p className="text-3xl font-bold text-white">{usage?.compute_hours?.toFixed(1) || '0.0'}</p>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-sm text-gray-500">This month</span>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Data Transfer</p>
                <p className="text-3xl font-bold text-white">{usage?.data_transfer_gb?.toFixed(1) || '0.0'} GB</p>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-sm text-gray-500">This month</span>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Storage</p>
                <p className="text-3xl font-bold text-white">{usage?.storage_gb?.toFixed(1) || '0.0'} GB</p>
              </div>
              <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-sm text-gray-500">Current usage</span>
            </div>
          </div>
        </div>

        {/* Billing Notice */}
        <div className="bg-blue-900/20 border border-blue-800/50 rounded-xl p-6 mb-8">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-white mb-1">Transparent Pricing</h3>
              <p className="text-blue-300 text-sm">
                All charges reflect direct cloud provider costs with zero PropAlgos markup. 
                You pay exactly what we pay for compute, storage, and data transfer.
              </p>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        {paymentMethods.length > 0 && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 mb-8">
            <div className="p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold text-white">Payment Methods</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {paymentMethods.map((method, index) => (
                  <div key={method.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-white font-medium">
                          {method.card?.brand.toUpperCase()} •••• {method.card?.last4}
                        </div>
                        <div className="text-sm text-gray-400">
                          Expires {method.card?.exp_month}/{method.card?.exp_year}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {index === 0 && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                          Default
                        </span>
                      )}
                      {paymentMethods.length > 1 && (
                        <button
                          onClick={() => handleRemovePaymentMethod(method.id)}
                          className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded-full transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
          </>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <>
            {/* Balance Transaction History */}
            {balanceTransactions.length > 0 && (
              <div className="bg-gray-900 rounded-xl border border-gray-800 mb-8">
                <div className="p-6 border-b border-gray-800">
                  <h2 className="text-xl font-bold text-white">Balance Transactions</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {balanceTransactions.map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            transaction.type === 'topup' ? 'bg-green-500/20' : 'bg-red-500/20'
                          }`}>
                            {transaction.type === 'topup' ? (
                              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <div className="text-white font-medium">
                              {transaction.description}
                            </div>
                            <div className="text-sm text-gray-400">
                              {new Date(transaction.created_at).toLocaleDateString()} at{' '}
                              {new Date(transaction.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${
                            transaction.amount > 0 ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {transaction.amount > 0 ? '+' : ''}${Math.abs(transaction.amount).toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-400">
                            Balance: ${transaction.balance_after.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Invoices History */}
            <div className="bg-gray-900 rounded-xl border border-gray-800">
              <div className="p-6 border-b border-gray-800">
                <h2 className="text-xl font-bold text-white">Invoice History</h2>
              </div>

              <div className="p-6">
                {invoices.length > 0 ? (
                  <div className="space-y-4">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <span className="text-lg mr-3">💰</span>
                            <div className="flex-1">
                              <h4 className="text-white font-medium">
                                {invoice.description || 'Usage charges'}
                              </h4>
                              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-400">
                                <span>{new Date(invoice.created * 1000).toLocaleDateString()}</span>
                                <span>•</span>
                                <span>Invoice #{invoice.id.slice(-8)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className={`px-2 py-1 text-xs rounded-full ${
                              invoice.status === 'paid' ? 'text-green-500 bg-green-500/20' :
                              invoice.status === 'open' ? 'text-yellow-500 bg-yellow-500/20' :
                              'text-red-500 bg-red-500/20'
                            }`}>
                              {invoice.status}
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-white">
                                ${(invoice.amount_paid / 100).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">No transactions yet</div>
                    <p className="text-sm text-gray-500">
                      Your billing history will appear here once you start using cloud resources.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <>
            {/* Time Range Selector */}
            <div className="flex justify-end mb-6">
              <select
                value={usageTimeRange}
                onChange={(e) => setUsageTimeRange(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </div>

            {/* Summary Cards */}
            {(() => {
              const rangeTotal = usageHistory.reduce((sum, d) => sum + d.cost, 0);
              const rangeHours = usageHistory.reduce((sum, d) => sum + d.compute_hours, 0);
              const avgDaily = usageHistory.length > 0 ? rangeTotal / usageHistory.length : 0;
              return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Total Cost ({usageTimeRange})</p>
                    <p className="text-3xl font-bold text-white">${rangeTotal.toFixed(2)}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Compute Hours ({usageTimeRange})</p>
                    <p className="text-3xl font-bold text-white">{rangeHours.toFixed(1)}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Avg Daily Cost</p>
                    <p className="text-3xl font-bold text-white">
                      ${avgDaily.toFixed(2)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
              );
            })()}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Daily Usage Chart */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <h3 className="text-lg font-bold text-white mb-6">Daily Usage Trend</h3>
                <div className="space-y-4">
                  {usageHistory.length > 0 ? (
                    usageHistory.slice(0, 10).map((day) => {
                      const maxHours = Math.max(...usageHistory.map(d => d.compute_hours), 10);
                      return (
                        <div key={day.date} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="text-sm text-gray-400 w-20">
                              {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                            <div className="flex-1 w-32">
                              <div className="w-full bg-gray-700 rounded-full h-3">
                                <div
                                  className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                                  style={{ width: `${Math.max((day.compute_hours / maxHours) * 100, 2)}%` }}
                                />
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-white">{day.compute_hours.toFixed(1)}h</div>
                              <div className="text-xs text-gray-400">${day.cost.toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">No usage data available</div>
                      <div className="text-sm text-gray-500">Start using cloud resources to see your usage trend here</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Resource Breakdown */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <h3 className="text-lg font-bold text-white mb-6">Resource Breakdown</h3>
                <div className="space-y-4">
                  {resourceBreakdown.length > 0 ? (
                    resourceBreakdown.map((resource) => (
                      <div key={resource.name} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-lg">
                              {resource.type === 'compute' ? '⚡' : resource.type === 'storage' ? '💾' : '📡'}
                            </span>
                            <div>
                              <div className="text-sm font-medium text-white">{resource.name}</div>
                              <div className="text-xs text-gray-400">
                                {resource.usage_amount.toFixed(1)} {resource.unit}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-white">${resource.cost.toFixed(2)}</div>
                            <div className="text-xs text-gray-400">{resource.percentage}%</div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              resource.type === 'compute' ? 'bg-blue-500' :
                              resource.type === 'storage' ? 'bg-green-500' : 'bg-purple-500'
                            }`}
                            style={{ width: `${Math.max(resource.percentage, 2)}%` }}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">No resource usage yet</div>
                      <div className="text-sm text-gray-500">Your resource breakdown will appear here once you start using cloud services</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cost Optimization Tips */}
            <div className="mt-8 bg-yellow-900/20 border border-yellow-800/50 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center mr-3 mt-1">
                  <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-white mb-2">Cost Optimization Tips</h3>
                  <div className="space-y-2 text-sm text-yellow-300">
                    <p>• Consider using Tesla T4 instances for lighter workloads - they're significantly cheaper than V100</p>
                    <p>• Stop instances when not in use - you only pay for active compute time</p>
                    <p>• Use spot instances when available for up to 70% savings on compute costs</p>
                    <p>• Monitor your usage patterns to right-size your resource allocations</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
}